import React from 'react';
import { BtnIcon, Typography } from '@growthaxl/web-foundation';
import type { OpenCommitment, OpenCommitmentOption } from '../../types';
import {
  matchSettledOpenCommitmentAnswer,
  optionLetter,
  wireTextForOpenCommitmentAnswer,
} from '../../utils/openCommitment';

interface OptionRowProps {
  letter: string;
  label: string;
  hint?: string;
  disabled: boolean;
  onPick: () => void;
}

interface OptionLetterBadgeProps {
  letter: string;
  /** When true, apply OptionRow hover invert via parent `group` class. */
  interactive?: boolean;
  testId?: string;
}

function OptionLetterBadge({
  letter,
  interactive = false,
  testId,
}: OptionLetterBadgeProps): React.ReactElement {
  return (
    <span
      className={[
        'shrink-0 inline-flex size-5 items-center justify-center',
        'rounded-[5px] border border-esa-accent/40 bg-esa-accent-muted',
        'font-mono text-[11px] font-bold leading-none text-esa-accent',
        interactive
          ? 'mt-0.5 transition-colors group-hover:border-esa-accent group-hover:bg-esa-accent group-hover:text-brand-on-primary'
          : '',
      ].join(' ')}
      aria-hidden="true"
      data-testid={testId}
    >
      {letter}
    </span>
  );
}

/**
 * One lettered option row. The whole row is the affordance — there is no
 * separate select button, so a click anywhere answers.
 */
function OptionRow({ letter, label, hint, disabled, onPick }: OptionRowProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      title={`Answer ${letter}`}
      aria-label={label}
      className={[
        'group flex w-full items-start gap-2.5 px-3 py-2.5 text-left',
        'border-t border-border-default first:border-t-0 bg-transparent',
        'transition-colors cursor-pointer hover:bg-esa-accent-muted',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent',
      ].join(' ')}
    >
      <OptionLetterBadge letter={letter} interactive />
      <span className="min-w-0 flex-1">
        <Typography variant="body2" color="primary">
          <span className="text-[13px] font-bold leading-snug">{label}</span>
        </Typography>
        {hint ? (
          <Typography variant="caption" color="secondary">
            <span className="mt-0.5 block text-[11px] leading-snug text-text-tertiary">{hint}</span>
          </Typography>
        ) : null}
      </span>
    </button>
  );
}

interface FreeTextAnswerRowProps {
  disabled: boolean;
  /** Next letter after the option list — visual alignment with A–E rows. */
  letter: string;
  onSubmit: (text: string) => void;
}

function FreeTextAnswerRow({
  disabled,
  letter,
  onSubmit,
}: FreeTextAnswerRowProps): React.ReactElement {
  const [draft, setDraft] = React.useState('');
  const canSend = draft.trim().length > 0 && !disabled;

  const submit = (): void => {
    const trimmed = draft.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setDraft('');
  };

  return (
    <div className="flex items-center gap-2.5 border-t border-border-default bg-transparent px-3 py-2">
      <OptionLetterBadge letter={letter} testId="open-commitment-free-letter" />
      <input
        type="text"
        data-testid="open-commitment-free-input"
        value={draft}
        disabled={disabled}
        placeholder="Enter your answer here"
        aria-label="Enter your answer here"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        className={[
          'min-w-0 flex-1 border-none bg-transparent text-[13px] text-text-primary outline-none',
          'placeholder:text-text-tertiary disabled:opacity-50',
        ].join(' ')}
      />
      <button
        type="button"
        data-testid="open-commitment-free-send"
        disabled={!canSend}
        aria-label="Send answer"
        onClick={submit}
        className={[
          'inline-flex size-7 shrink-0 items-center justify-center rounded-[5px]',
          'text-esa-accent transition-colors',
          'hover:bg-esa-accent-muted disabled:cursor-not-allowed disabled:opacity-40',
        ].join(' ')}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
        </svg>
      </button>
    </div>
  );
}

/** Footer line telling the user they can type the letter instead of clicking. */
function footerHint(commitment: OpenCommitment, options: OpenCommitmentOption[]): string {
  if (commitment.free_hint) {
    return commitment.free_hint;
  }
  const freeText = commitment.allow_free === false ? '' : ' — or answer in your own words';
  if (options.length < 2) {
    return `Pick it${freeText || ', or answer in your own words'}.`;
  }
  const first = optionLetter(options[0], 0);
  const last = optionLetter(options[options.length - 1], options.length - 1);
  return `Pick one, or just type ${first}–${last}${freeText}.`;
}

function SettledQuestion({ question }: { question: string }): React.ReactElement {
  return (
    <Typography variant="body2" color="primary">
      <p className="mb-2 text-[14px] font-semibold leading-relaxed">{question}</p>
    </Typography>
  );
}

export interface OpenCommitmentCardProps {
  commitment: OpenCommitment;
  onSelect: (option: string) => void;
  disabled?: boolean;
  /**
   * When true, skip repeating `commitment.question` on the live card (agent prose
   * already shows it). Settled turns always show the question for context.
   */
  hideQuestion?: boolean;
  /**
   * The reply that already answered this ask. Present on any turn the user has
   * moved past, which collapses the alternatives so an earlier turn cannot be
   * re-answered and fork the flow — it reads as a record of the decision.
   */
  settledWith?: string;
}

/**
 * Renders a DESO open ask as a lettered option list — the affordance sits in
 * the conversation flow, and the user either clicks a row or types the letter.
 * Click sends the option label as a normal converse message, which is what
 * DESO's option matching compares against.
 */
export function OpenCommitmentCard({
  commitment,
  onSelect,
  disabled = false,
  hideQuestion = false,
  settledWith,
}: OpenCommitmentCardProps): React.ReactElement | null {
  const options = commitment.options ?? [];

  if (options.length === 0) {
    return null;
  }

  const answer = (settledWith ?? '').trim();
  const question = (commitment.question ?? '').trim();
  const dataAttrs = {
    'data-testid': 'open-commitment-card',
    'data-oc-kind': commitment.kind || undefined,
    'data-oc-frame': commitment.frame_id || undefined,
  };

  if (answer) {
    const settlement = matchSettledOpenCommitmentAnswer(answer, options);
    const isOption = settlement.kind === 'option';

    return (
      <div {...dataAttrs} data-oc-settled={isOption ? 'option' : 'free-text'} className="mt-2 max-w-[420px]">
        {question ? <SettledQuestion question={question} /> : null}
        <div className="flex items-center gap-2 rounded-8 border border-border-default bg-surface-muted px-3 py-2">
          <BtnIcon name="check" size={10} className="shrink-0 text-success" />
          <div className="min-w-0 flex-1">
            <Typography variant="body2" color="primary">
              <span className={isOption ? 'font-bold' : 'font-normal'}>
                {isOption ? (
                  <>
                    <span className="mr-1 font-mono text-esa-accent">
                      {optionLetter(settlement.option, settlement.index)}
                    </span>
                    {settlement.option.label}
                  </>
                ) : (
                  settlement.text
                )}
              </span>
            </Typography>
            <Typography variant="caption" color="secondary">
              <span className="mt-px block">
                {isOption ? 'Option selected' : 'Your reply — recorded'}
              </span>
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  const showQuestion = !hideQuestion && question.length > 0;
  const total = commitment.total ?? 0;
  const showCounter = total > 1;

  return (
    <div {...dataAttrs} className="mt-2 max-w-[420px]">
      {showQuestion ? <SettledQuestion question={question} /> : null}
      {showCounter ? (
        <Typography variant="caption" color="secondary">
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-text-tertiary">
            {`Question ${(commitment.index ?? 0) + 1} of ${total}`}
          </p>
        </Typography>
      ) : null}
      <div
        className="flex flex-col overflow-hidden rounded-8 border border-border-default bg-surface-card"
        role="group"
        aria-label={question || 'Answer options'}
      >
        {options.map((option, i) => (
          <OptionRow
            key={option.id ?? option.label}
            letter={optionLetter(option, i)}
            label={option.label}
            hint={option.hint}
            disabled={disabled}
            onPick={() => onSelect(option.label)}
          />
        ))}
        {commitment.allow_free === false ? null : (
          <FreeTextAnswerRow
            disabled={disabled}
            letter={optionLetter({ label: '' }, options.length)}
            onSubmit={(text) => onSelect(wireTextForOpenCommitmentAnswer(text, commitment))}
          />
        )}
      </div>
      <Typography variant="caption" color="secondary">
        <p className="mt-2 text-[11px] leading-snug text-text-tertiary">
          {footerHint(commitment, options)}
        </p>
      </Typography>
    </div>
  );
}
